/**
 * Designer Workspace — pure presentation logic, extracted from
 * `DesignerWorkspace.tsx` so it is unit-testable without importing React. No
 * fetching, no permission gating — display config + pure transforms only. The
 * FE gate (designer ROLE HOME — any designer, no client-count condition) lives
 * in `AppLanding`; RLS stays the load-bearing gate.
 *
 * Scope note (Task 212): the workspace is driven by the ASSIGNED-ONLY feed
 * `list-my-production-tasks` (RLS assignee-match), NOT the org-wide client
 * list. Task selection drives the detail + brand panes; the brand-kit pane is
 * scoped to the SELECTED TASK's client. The production brief in the detail pane
 * comes from the existing 108A/108B contract via `list-content-variants` +
 * `productionBrief.ts`.
 *
 * Re-exports the shared display helpers from `ownerCommandCenter.logic` (clean
 * DRY reuse of pure functions). The config tables + transforms below are
 * designer-specific.
 */

import type {
  ContentDiscipline,
  ContentVariantPlatform,
  ContentVariantRow,
  MyProductionTask,
  MyProductionTaskStatus,
} from '../api/types';

export {
  SOON,
  clientStatusLabelAr,
  statusBadgeClass,
  avatarColor,
  initial,
} from './ownerCommandCenter.logic';

/**
 * Task-queue groups by the REAL `task_status` lifecycle returned by
 * `list-my-production-tasks` (Task 118). The feed carries no per-task deadline,
 * so date-based grouping (overdue/today/upcoming) is intentionally NOT done
 * here — we group by the actual task status the data supports.
 */
export interface TaskStatusGroup {
  readonly key: MyProductionTaskStatus;
  readonly label: string;
  readonly tone: 'new' | 'prog' | 'review' | 'blocked';
}
export const TASK_STATUS_GROUPS: ReadonlyArray<TaskStatusGroup> = [
  { key: 'open', label: 'جديدة', tone: 'new' },
  { key: 'in_progress', label: 'قيد التنفيذ', tone: 'prog' },
  { key: 'submitted', label: 'بانتظار المراجعة', tone: 'review' },
  { key: 'blocked', label: 'محظورة / بانتظار معلومات', tone: 'blocked' },
];

/** Arabic label for a single task status (mirror of the group labels). */
export function taskStatusLabelAr(status: MyProductionTaskStatus): string {
  const g = TASK_STATUS_GROUPS.find((x) => x.key === status);
  return g ? g.label : status;
}

/** One status group with its matching tasks (preserves feed order within). */
export interface TaskGroup {
  readonly key: MyProductionTaskStatus;
  readonly label: string;
  readonly tone: string;
  readonly tasks: ReadonlyArray<MyProductionTask>;
}

/** Bucket the feed into the ordered status groups. Empty groups are kept so
 *  the caller can decide whether to render an empty bucket or hide it. */
export function groupTasksByStatus(
  tasks: ReadonlyArray<MyProductionTask>,
): ReadonlyArray<TaskGroup> {
  return TASK_STATUS_GROUPS.map((g) => ({
    key: g.key,
    label: g.label,
    tone: g.tone,
    tasks: tasks.filter((t) => t.task_status === g.key),
  }));
}

/**
 * Presentation LANES (Task 214) — re-frame the status groups so the designer's
 * ACTIONABLE work is primary and completed/submitted work reads as history.
 * Pure presentation; never changes the feed or any task status.
 *   - `actionable`: `open` + `in_progress` (the designer can act: Start /
 *     upload+Submit). PRIMARY.
 *   - `review`: `submitted` (done — awaiting the REVIEWER, not the designer).
 *     De-emphasized.
 *   - `blocked`: `blocked`. De-emphasized.
 */
export type QueueLaneKey = 'actionable' | 'review' | 'blocked';
export interface QueueLane {
  readonly key: QueueLaneKey;
  readonly label: string;
  readonly statuses: ReadonlyArray<MyProductionTaskStatus>;
  readonly deemphasized: boolean;
}
export const QUEUE_LANES: ReadonlyArray<QueueLane> = [
  {
    key: 'actionable',
    label: 'مطلوب منك الآن',
    statuses: ['open', 'in_progress'],
    deemphasized: false,
  },
  { key: 'review', label: 'بانتظار المراجعة', statuses: ['submitted'], deemphasized: true },
  { key: 'blocked', label: 'محظورة / بانتظار معلومات', statuses: ['blocked'], deemphasized: true },
];

/** One lane with its matching tasks (ordered by the lane's status order, then
 *  feed order within each status). */
export interface TaskLane {
  readonly key: QueueLaneKey;
  readonly label: string;
  readonly deemphasized: boolean;
  readonly tasks: ReadonlyArray<MyProductionTask>;
}
export function groupTasksByLane(tasks: ReadonlyArray<MyProductionTask>): ReadonlyArray<TaskLane> {
  return QUEUE_LANES.map((lane) => ({
    key: lane.key,
    label: lane.label,
    deemphasized: lane.deemphasized,
    tasks: lane.statuses.flatMap((s) => tasks.filter((t) => t.task_status === s)),
  }));
}

/** Count of tasks the designer can ACT on now (`open` + `in_progress`). */
export function actionableCount(tasks: ReadonlyArray<MyProductionTask>): number {
  return tasks.filter((t) => t.task_status === 'open' || t.task_status === 'in_progress').length;
}

/** Count of the designer's `submitted` tasks (done — awaiting reviewer). */
export function awaitingReviewCount(tasks: ReadonlyArray<MyProductionTask>): number {
  return tasks.filter((t) => t.task_status === 'submitted').length;
}

/**
 * True when the designer has NOTHING to act on but DOES have submissions in
 * review — drives the «لا يوجد عمل مطلوب منك الآن» empty state. False for a
 * fully empty feed (that has its own "no tasks assigned" empty state).
 */
export function isNothingActionable(tasks: ReadonlyArray<MyProductionTask>): boolean {
  return actionableCount(tasks) === 0 && awaitingReviewCount(tasks) > 0;
}

/** Real workload counts derived from the feed (no fabricated stats). */
export interface WorkloadCounts {
  readonly total: number;
  readonly open: number;
  readonly in_progress: number;
  readonly submitted: number;
  readonly blocked: number;
}
export function computeWorkloadStats(tasks: ReadonlyArray<MyProductionTask>): WorkloadCounts {
  return {
    total: tasks.length,
    open: tasks.filter((t) => t.task_status === 'open').length,
    in_progress: tasks.filter((t) => t.task_status === 'in_progress').length,
    submitted: tasks.filter((t) => t.task_status === 'submitted').length,
    blocked: tasks.filter((t) => t.task_status === 'blocked').length,
  };
}

/** Workload-strip config — each chip reads a real count key from
 *  `computeWorkloadStats`. No date/`delivered` stats (the feed lacks them). */
export interface WorkloadStat {
  readonly key: keyof WorkloadCounts;
  readonly label: string;
  readonly tone: string;
}
export const WORKLOAD_STATS: ReadonlyArray<WorkloadStat> = [
  { key: 'total', label: 'إجمالي المهام', tone: 'wi-sky' },
  { key: 'open', label: 'جديدة', tone: 'wi-amber' },
  { key: 'in_progress', label: 'قيد التنفيذ', tone: 'wi-sky' },
  { key: 'submitted', label: 'بانتظار المراجعة', tone: 'wi-plum' },
  { key: 'blocked', label: 'محظورة', tone: 'wi-rose' },
];

/** Arabic discipline label (mirror of EmployeeTasksPage's DisciplinePill). */
export function disciplineLabelAr(discipline: ContentDiscipline): string {
  switch (discipline) {
    case 'design':
      return 'تصميم';
    case 'video':
      return 'فيديو';
    case 'content':
      return 'محتوى';
    default:
      return discipline;
  }
}

/**
 * Arabic platform label for a content-variant platform chip. Mirror of the
 * label scheme used elsewhere (e.g. AgencyKnowledgeLibraryPage) — kept here so
 * the platform-name literals stay out of the React component (whose shape tests
 * intentionally forbid integration platform names). Display-only.
 */
export function platformLabelAr(platform: ContentVariantPlatform): string {
  switch (platform) {
    case 'instagram_feed':
      return 'Instagram · منشور';
    case 'instagram_story':
      return 'Instagram · ستوري';
    case 'instagram_reel':
      return 'Instagram · ريل';
    case 'facebook_post':
      return 'Facebook · منشور';
    case 'facebook_story':
      return 'Facebook · ستوري';
    case 'tiktok_video':
      return 'TikTok · فيديو';
    case 'snapchat_story':
      return 'Snapchat · ستوري';
    default:
      return platform;
  }
}

/** Arabic assignment-source label from the assignment metadata jsonb. */
export function assignmentSourceLabelAr(metadata: Readonly<Record<string, unknown>>): string {
  const src = metadata['assignment_source'];
  if (src === 'auto_on_handoff') return 'تعيين تلقائي';
  if (src === 'manual') return 'تعيين يدوي';
  return 'مصدر غير معروف';
}

/**
 * Resolve the selected task by `assignment_id`. Falls back to the first task so
 * the detail/brand panes populate on load. Returns null when there are no
 * tasks (or none match after filtering).
 */
export function resolveSelectedTask(
  tasks: ReadonlyArray<MyProductionTask>,
  selectedId: string | null,
): MyProductionTask | null {
  if (tasks.length === 0) return null;
  if (selectedId) {
    const found = tasks.find((t) => t.assignment_id === selectedId);
    if (found) return found;
  }
  return tasks[0] ?? null;
}

/** A client option for the queue filter, with its task count. */
export interface TaskClientOption {
  readonly id: string;
  readonly name: string;
  readonly count: number;
}

/**
 * Distinct clients present IN THE FEED (assigned-only — derived from the
 * designer's own tasks, never an org-wide client list). Order is first-seen.
 */
export function distinctClientsFromTasks(
  tasks: ReadonlyArray<MyProductionTask>,
): ReadonlyArray<TaskClientOption> {
  const byId = new Map<string, { id: string; name: string; count: number }>();
  for (const t of tasks) {
    if (!t.client) continue;
    const existing = byId.get(t.client.id);
    if (existing) existing.count += 1;
    else byId.set(t.client.id, { id: t.client.id, name: t.client.name, count: 1 });
  }
  return Array.from(byId.values());
}

/** Filter the feed by client id; `null` returns all tasks. */
export function filterTasksByClient(
  tasks: ReadonlyArray<MyProductionTask>,
  clientId: string | null,
): ReadonlyArray<MyProductionTask> {
  if (!clientId) return tasks;
  return tasks.filter((t) => t.client?.id === clientId);
}

/**
 * The earliest non-archived scheduled publish DATE (`YYYY-MM-DD`) for a
 * variant, or null when nothing is scheduled. Date-portion only of the
 * `scheduled_for` timestamptz — a read-only hint surfaced in the detail pane;
 * deliberately NOT used for queue grouping (the task feed carries no deadline).
 */
export function nextScheduledDate(variant: ContentVariantRow): string | null {
  const entries = variant.calendar_entries.filter(
    (e) =>
      e.archived_at === null && typeof e.scheduled_for === 'string' && e.scheduled_for.length > 0,
  );
  if (entries.length === 0) return null;
  const earliest = entries.reduce((a, b) => (a.scheduled_for <= b.scheduled_for ? a : b));
  return earliest.scheduled_for.slice(0, 10);
}

/**
 * Brand-kit sections — READ-ONLY brand identity, scoped to the selected task's
 * client. Designers consume the brand kit; they never edit it here. (No
 * brand-identity endpoint exists yet — the pane shows honest placeholders.)
 */
export interface BrandSection {
  readonly key: string;
  readonly label: string;
  readonly hint: string;
}
export const BRAND_SECTIONS: ReadonlyArray<BrandSection> = [
  { key: 'colors', label: 'الألوان', hint: 'لوحة ألوان العلامة' },
  { key: 'fonts', label: 'الخطوط', hint: 'خطوط العناوين والمتن' },
  { key: 'tone', label: 'النبرة والأسلوب', hint: 'نبرة الكتابة والمزاج البصري' },
  { key: 'rules', label: 'افعل / لا تفعل', hint: 'قواعد استخدام الهوية' },
  { key: 'approved', label: 'أعمال معتمدة سابقة', hint: 'مراجع من أعمال سابقة' },
  { key: 'assets', label: 'الأصول والشعارات', hint: 'الشعارات والملفات المصدرية' },
];

/**
 * Which designer actions are AVAILABLE for a given task status (pure — drives
 * button enablement). Mirrors the 101D state machine + Task 215:
 *   - `start`: `open` | `blocked` → `in_progress`
 *   - `submit` (upload+submit, coupled): only when `in_progress`
 *   - `clarify`: a question — any active status (`open`/`in_progress`/`blocked`)
 *   - `block`: work the designer can't proceed on (`open`/`in_progress`; a
 *     blocked task is already blocked). A `submitted` task is done → none.
 */
export interface ActionAvailability {
  readonly start: boolean;
  readonly submit: boolean;
  readonly clarify: boolean;
  readonly block: boolean;
}
export function allowedActions(status: MyProductionTaskStatus): ActionAvailability {
  return {
    start: status === 'open' || status === 'blocked',
    submit: status === 'in_progress',
    clarify: status === 'open' || status === 'in_progress' || status === 'blocked',
    block: status === 'open' || status === 'in_progress',
  };
}

/**
 * Read the designer-visible block reason out of the assignment metadata
 * (written by `block_production_task_v1` into `task_assignments.metadata`).
 * Null when absent/blank. Surfaced read-only in the Task 214 blocked lane.
 */
export function readBlockReason(metadata: Readonly<Record<string, unknown>>): string | null {
  const r = metadata['block_reason'];
  return typeof r === 'string' && r.trim().length > 0 ? r.trim() : null;
}

/** Default submission title from a filename: strip the extension + trim, with a
 *  safe non-empty fallback. */
export function titleFromFilename(filename: string): string {
  const base = filename.replace(/\.[^./\\]+$/, '').trim();
  return base.length > 0 ? base : 'تسليم';
}

/** A stashed prepared-upload pointer, preserved across submit retries. */
export interface PendingSubmit {
  readonly taskId: string;
  readonly storageKey: string;
  readonly fileName: string;
  readonly fileSize: number;
  readonly fileLastModified: number;
}

/**
 * Whether a stashed pending-submit may be REUSED for the current (task, file)
 * on a submit retry (101D idempotency). Matches STRICTLY on task identity plus
 * the full file fingerprint (name + size + lastModified) so a user who swaps in
 * a DIFFERENT file with a coincidentally identical name+size between attempts
 * does not silently submit the previously-uploaded bytes.
 */
export function pendingSubmitMatches(
  pending: PendingSubmit | null,
  args: {
    readonly taskId: string;
    readonly fileName: string;
    readonly fileSize: number;
    readonly fileLastModified: number;
  },
): boolean {
  return (
    pending !== null &&
    pending.taskId === args.taskId &&
    pending.fileName === args.fileName &&
    pending.fileSize === args.fileSize &&
    pending.fileLastModified === args.fileLastModified
  );
}

/**
 * The designer-facing linear lifecycle for the state-transition display.
 * `blocked` is intentionally NOT a linear step — it's a side state the renderer
 * shows separately.
 */
export interface LifecycleStep {
  readonly key: MyProductionTaskStatus;
  readonly label: string;
}
export const TASK_LIFECYCLE: ReadonlyArray<LifecycleStep> = [
  { key: 'open', label: 'جديدة' },
  { key: 'in_progress', label: 'قيد التنفيذ' },
  { key: 'submitted', label: 'بانتظار المراجعة' },
];

/** Index of a status within `TASK_LIFECYCLE`, or -1 for a non-linear state
 *  (e.g. `blocked`). Lets the renderer mark done / current / upcoming steps. */
export function lifecycleIndex(status: MyProductionTaskStatus): number {
  return TASK_LIFECYCLE.findIndex((s) => s.key === status);
}
